import React, { useEffect } from 'react';
import { withOktaAuth } from '@okta/okta-react';
import type { OktaContext } from '@okta/okta-react';

type IOktaContext = React.ContextType<typeof OktaContext>;

import { Stack } from 'ui/stack';

import { Loader } from 'ui/loader';
import NotificationMessage from 'components/partials/notification/notification';
import SkipLink from 'ui/skip-link';
import { SideMenuLayout } from 'components/shared/side-menu';
import MainAuthenticated from 'components/shared/others/main-authenticated/main-authenticated';
import Footer from 'components/shared/others/footer/footer';
import { MarkPanelProvider } from 'containers/admin/clients/client/tabs-components/ai-assistant/mark-panel/mark-panel-context';
import MarkPanelHost from 'containers/admin/clients/client/tabs-components/ai-assistant/mark-panel/components/mark-panel-host';
import MonitoringIntroPopover from 'containers/user/critical-elements-monitoring/walkthrough/monitoring-intro-popover';

import { useCurrentUser } from 'hooks/api/use-current-user';
import usePageTitle from 'hooks/use-page-title';

// TODO: Wave 8 — withOktaAuth HOC injects { oktaAuth, authState }; props match
// the IOktaContext shape Okta passes. The component body doesn't read any of
// those props directly today.
type AuthenticatedAppProps = Partial<IOktaContext>;

const AuthenticatedApp: React.FC<AuthenticatedAppProps> = () => {
  const user = useCurrentUser();

  usePageTitle();

  useEffect(() => {
    if (!user.data || !user.id) {
      user.getInfo();
    }
  }, [user.id, user.data]);

  if (user.error) {
    const userError = user.error as
      | { data?: { detail?: string; title?: string } }
      | undefined;

    return (
      <NotificationMessage
        customClass="m-8"
        variant="error"
        heading="An error occurred"
        message={
          (userError && userError.data && userError.data.detail) ||
          (userError && userError.data && userError.data.title)
        }
      />
    );
  }

  return (
    <>
      <SkipLink />
      {user.loading || !user.id || !user.data ? (
        <Loader loading />
      ) : (
        <MarkPanelProvider>
          <SideMenuLayout rightDock={<MarkPanelHost />}>
            <div className="w-full flex-1 flex flex-col font-text font-normal leading-normal text-base p-0 text-gray-700">
              <Stack direction="row" sx={{ flex: '1 1 0%', pb: 2 }}>
                {/* TODO: Wave 8 — MainAuthenticated expects a stricter user
                    shape than CurrentUserState exposes. The runtime contract
                    has worked since the JS days; cast is a temporary bridge. */}
                <MainAuthenticated user={user as never} />
              </Stack>
              <Footer />
            </div>
            <MonitoringIntroPopover />
          </SideMenuLayout>
        </MarkPanelProvider>
      )}
    </>
  );
};

export default (
  withOktaAuth as unknown as (
    c: React.ComponentType<unknown>
  ) => React.ComponentType<unknown>
)(AuthenticatedApp as React.ComponentType<unknown>);
